import type { IApp } from '@todesktop/shared';

export interface DocLink {
  key: string;
  label: string;
  url: string;
}

export interface DocGroup {
  key: 'electron' | 'builder';
  label: string;
  links: DocLink[];
}

const ELECTRON_DOCS: DocGroup = {
  key: 'electron',
  label: 'Electron',
  links: [
    {
      key: 'electron-docs',
      label: 'Documentation',
      url: 'https://www.todesktop.com/electron/docs',
    },
    {
      key: 'cli-readme',
      label: 'CLI readme',
      url: 'https://www.npmjs.com/package/@todesktop/cli',
    },
    {
      key: 'runtime-readme',
      label: 'Runtime readme',
      url: 'https://www.npmjs.com/package/@todesktop/runtime',
    },
  ],
};

const BUILDER_DOCS: DocGroup = {
  key: 'builder',
  label: 'ToDesktop Builder',
  links: [
    {
      key: 'builder-docs',
      label: 'Documentation',
      url: 'https://www.todesktop.com/docs',
    },
  ],
};

export const STATUS_PAGE_URL = 'https://status.todesktop.com';

export type DocsProduct = 'electron' | 'builder';

/**
 * The docs an app's owner needs. Builder apps, and the older no-code apps
 * that came before it, use the Builder docs.
 */
export const getAppDocsProduct = (app: IApp): DocsProduct =>
  app.appType === 'electron' ? 'electron' : 'builder';

/**
 * Which docs to search: the open app's product, or, outside an app, the one
 * product all of the account's apps use. Null means search both.
 */
export const getDocsScope = (
  apps: IApp[],
  openApp: IApp | null | undefined,
): DocsProduct | null => {
  if (openApp) return getAppDocsProduct(openApp);
  const products = new Set(apps.map(getAppDocsProduct));
  return products.size === 1 ? [...products][0] : null;
};

/**
 * Docs relevant to the user's apps, with the current app's type first. Users
 * with no apps yet see both sets.
 */
export const getDocGroups = (
  apps: IApp[],
  currentApp: IApp | null | undefined,
): DocGroup[] => {
  const hasAnyApps = apps.length > 0;
  const products = new Set(apps.map(getAppDocsProduct));
  const showElectron = !hasAnyApps || products.has('electron');
  const showBuilder = !hasAnyApps || products.has('builder');

  const groups: DocGroup[] = [];
  if (showElectron) groups.push(ELECTRON_DOCS);
  if (showBuilder) groups.push(BUILDER_DOCS);

  if (currentApp && getAppDocsProduct(currentApp) === 'builder') {
    groups.reverse();
  }
  return groups;
};
